import api from './api';
import {
  CONFIG_CAMPO_CLIENTE_SUSPENDIDO_BLOQUEO,
  CONFIG_SECCION_VENTAS,
  parseBloqueoClienteSuspendidoFactura,
  type BloqueoClienteSuspendidoModo,
} from '../utils/clienteEstadoFacturacion';

export interface ConfiguracionDto {
  usuario: string;
  seccion: string;
  campo: string;
  valor?: string | null;
  comentario?: string | null;
}

export interface ConfiguracionSearchParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  usuario?: string;
  seccion?: string;
  campo?: string;
}

export interface ConfiguracionPagedResult {
  items: ConfiguracionDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const SECCION_VENTAS = 'VENTAS';
const CAMPO_CONTADO_PAGO_OBLIGATORIO = 'CONTADO:PAGOOBLIGATORIO';

const normalizeConfiguracion = (raw: Record<string, unknown>): ConfiguracionDto => ({
  usuario: String(raw.usuario ?? raw.Usuario ?? ''),
  seccion: String(raw.seccion ?? raw.Seccion ?? ''),
  campo: String(raw.campo ?? raw.Campo ?? ''),
  valor: (raw.valor ?? raw.Valor ?? null) as string | null,
  comentario: (raw.comentario ?? raw.Comentario ?? null) as string | null,
});

const configuracionService = {
  getConfiguraciones: async (params: ConfiguracionSearchParams): Promise<ConfiguracionPagedResult> => {
    const resp = await api.get('/api/Configuraciones', {
      params: {
        SearchTerm: params.searchTerm || undefined,
        Usuario: params.usuario || undefined,
        Seccion: params.seccion || undefined,
        Campo: params.campo || undefined,
        PageNumber: params.pageNumber ?? 1,
        PageSize: params.pageSize ?? 20,
      },
    });
    const data = resp.data;
    if (data && typeof data === 'object' && 'items' in data) {
      const paged = data as ConfiguracionPagedResult & { Items?: unknown[] };
      const items = (paged.items ?? paged.Items ?? []) as unknown[];
      return {
        items: items.map((item) => normalizeConfiguracion(item as Record<string, unknown>)),
        totalCount: paged.totalCount ?? (data as { TotalCount?: number }).TotalCount ?? 0,
        pageNumber: paged.pageNumber ?? (data as { PageNumber?: number }).PageNumber ?? 1,
        pageSize: paged.pageSize ?? (data as { PageSize?: number }).PageSize ?? 20,
        totalPages: paged.totalPages ?? (data as { TotalPages?: number }).TotalPages ?? 0,
      };
    }
    return {
      items: [],
      totalCount: 0,
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 20,
      totalPages: 0,
    };
  },

  /** Recorre páginas para armar el set de flags del tenant (permisos). */
  listAll: async (params: Omit<ConfiguracionSearchParams, 'pageNumber' | 'pageSize'> = {}): Promise<ConfiguracionDto[]> => {
    const pageSize = 100;
    const acc: ConfiguracionDto[] = [];
    let pageNumber = 1;
    let totalPages = 1;
    while (pageNumber <= totalPages && pageNumber <= 15) {
      const page = await configuracionService.getConfiguraciones({
        ...params,
        pageNumber,
        pageSize,
      });
      acc.push(...page.items);
      totalPages = Math.max(1, page.totalPages || 1);
      if (page.items.length < pageSize) break;
      pageNumber += 1;
    }
    return acc;
  },

  getSecciones: async (): Promise<string[]> => {
    const resp = await api.get<string[]>('/api/Configuraciones/secciones');
    const data = resp.data;
    if (!Array.isArray(data)) return [];
    return data.map((s) => String(s).trim()).filter(Boolean);
  },

  getByKey: async (
    seccion: string,
    campo: string,
    usuario = '',
  ): Promise<ConfiguracionDto | null> => {
    try {
      const resp = await api.get<ConfiguracionDto>('/api/Configuraciones/by-key', {
        params: { usuario, seccion, campo },
      });
      if (!resp?.data) return null;
      return normalizeConfiguracion(resp.data as unknown as Record<string, unknown>);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw error;
    }
  },

  updateConfiguracion: async (dto: ConfiguracionDto): Promise<void> => {
    await api.put('/api/Configuraciones', {
      usuario: dto.usuario ?? '',
      seccion: dto.seccion,
      campo: dto.campo,
      valor: dto.valor ?? null,
      comentario: dto.comentario ?? null,
    });
  },

  createConfiguracion: async (dto: ConfiguracionDto): Promise<void> => {
    await api.post('/api/Configuraciones', {
      usuario: dto.usuario ?? '',
      seccion: dto.seccion,
      campo: dto.campo,
      valor: dto.valor ?? null,
      comentario: dto.comentario ?? null,
    });
  },

  /** Valor=1: pago obligatorio en CONTADO. Default true si no existe o hay error. */
  isContadoPagoObligatorio: async (): Promise<boolean> => {
    try {
      const config = await configuracionService.getByKey(
        SECCION_VENTAS,
        CAMPO_CONTADO_PAGO_OBLIGATORIO,
      );
      if (!config) return true;
      return String(config.valor ?? '').trim() === '1';
    } catch {
      return true;
    }
  },

  /** ALERT (default) | SOFT | HARD — facturación con cliente suspendido. */
  getClienteSuspendidoBloqueoFactura: async (): Promise<BloqueoClienteSuspendidoModo> => {
    try {
      const config = await configuracionService.getByKey(
        CONFIG_SECCION_VENTAS,
        CONFIG_CAMPO_CLIENTE_SUSPENDIDO_BLOQUEO,
      );
      return parseBloqueoClienteSuspendidoFactura(config?.valor);
    } catch {
      return 'alert';
    }
  },
};

export default configuracionService;
